import { useEffect, useMemo, useRef, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Trash2, Download, FileText, FileImage, FileSpreadsheet, File as FileIcon, AlertTriangle, Link2, Sparkles, Wand2 } from "lucide-react";
import { useLists } from "@/hooks/useLists";
import { useAuth } from "@/context/AuthContext";

// legacy fallback map for docs uploaded before admin-editable categories
const LEGACY_CAT_LABEL = {
  fatture_attive: "Fatture attive", fatture_passive: "Fatture passive", contratti: "Contratti",
  chiusure_fiscali: "Chiusure fiscali", certificati_haccp: "Certificati HACCP", corsi_formazione: "Corsi & formazione",
  dipendenti: "Documenti dipendenti", assicurazioni: "Assicurazioni", autorizzazioni: "Autorizzazioni & licenze",
  ricevute: "Ricevute", manutenzioni: "Manutenzioni", altri: "Altri",
};
const DEFAULT_DOC_CATS = ["Fatture attive", "Fatture passive", "Contratti", "Chiusure fiscali", "Certificati HACCP", "Corsi & formazione", "Documenti dipendenti", "Assicurazioni", "Autorizzazioni & licenze", "Ricevute", "Manutenzioni", "Altri"];

function fileIcon(ct) {
  if (!ct) return FileIcon;
  if (ct.includes("pdf")) return FileText;
  if (ct.includes("image")) return FileImage;
  if (ct.includes("spreadsheet") || ct.includes("excel") || ct.includes("csv")) return FileSpreadsheet;
  return FileIcon;
}
function fmtBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function Documents() {
  const { lists } = useLists();
  // FASE 7.4 STEP E.1 — documents action guards.
  const { can } = useAuth();
  const canWrite = can("documents.write");
  const canDelete = can("documents.delete");
  const CATEGORY_LIST = lists?.document_categories?.length ? lists.document_categories : DEFAULT_DOC_CATS;
  const catLabel = (k) => LEGACY_CAT_LABEL[k] || k;
  const [items, setItems] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [expOnly, setExpOnly] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "Fatture passive", notes: "", expiry_date: "", transaction_id: "", employee_id: "", supplier_id: "", tags: "" });
  const [file, setFile] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrData, setOcrData] = useState(null);
  const [txDialog, setTxDialog] = useState(null); // {extracted, doc}
  const fileRef = useRef();

  const load = async () => {
    const params = new URLSearchParams();
    if (cat !== "all") params.set("category", cat);
    if (q) params.set("q", q);
    if (expOnly) params.set("expiring_days", "30");
    const [d, tx, em, sp, ac] = await Promise.all([
      api.get(`/documents?${params.toString()}`),
      api.get("/transactions"),
      api.get("/employees"),
      api.get("/suppliers"),
      api.get("/accounts"),
    ]);
    setItems(d.data);
    setTransactions(tx.data);
    setEmployees(em.data);
    setSuppliers(sp.data);
    setAccounts(ac.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, expOnly]);
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Seleziona un file"); return; }
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(form).forEach(([k, v]) => { if (v != null && v !== "") fd.append(k, v); });
    if (!form.name) fd.set("name", file.name);
    if (ocrData) fd.append("ocr", JSON.stringify(ocrData));
    try {
      const res = await fetch(`${API_BASE}/documents`, { method: "POST", body: fd, credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const newDoc = await res.json();
      toast.success("Documento caricato");
      setOpen(false);
      setForm({ name: "", category: CATEGORY_LIST[0] || "Altri", notes: "", expiry_date: "", transaction_id: "", employee_id: "", supplier_id: "", tags: "" });
      setFile(null);
      // if OCR data available and no transaction linked → open tx dialog directly
      if (ocrData && ocrData.total_amount != null && !form.transaction_id) {
        setTxDialog({ doc: newDoc, extracted: ocrData });
      }
      setOcrData(null);
      load();
    } catch (err) { toast.error("Errore upload"); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Eliminare "${name}"?`)) return;
    await api.delete(`/documents/${id}`);
    toast.success("Eliminato"); load();
  };

  const download = (id, filename) => {
    fetch(`${API_BASE}/documents/${id}/download`, { credentials: "include" })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = filename || "documento"; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const runOcr = async () => {
    if (!file) { toast.error("Seleziona un PDF"); return; }
    if (!file.type?.includes("pdf") && !file.name?.toLowerCase().endsWith(".pdf")) {
      toast.error("OCR disponibile solo per PDF");
      return;
    }
    setOcrLoading(true);
    setOcrData(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_BASE}/documents/ocr`, { method: "POST", body: fd, credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const ex = data.extracted || {};
      setOcrData(ex);
      // pre-fill form fields
      const parts = [];
      if (ex.supplier) parts.push(ex.supplier);
      if (ex.invoice_number) parts.push(`Fatt. ${ex.invoice_number}`);
      setForm((f) => ({
        ...f,
        name: parts.join(" · ") || f.name,
        expiry_date: f.expiry_date,
        notes: (ex.description || f.notes || "").slice(0, 300),
        category: "fatture_passive",
      }));
      toast.success("Dati estratti dalla fattura");
    } catch (err) {
      const msg = String(err.message || err);
      toast.error(msg.length > 120 ? "Errore OCR — vedi console" : msg);
      console.error("OCR error:", msg);
    } finally {
      setOcrLoading(false);
    }
  };

  const createTxFromOcr = async (doc, extracted) => {
    setTxDialog({ doc, extracted });
  };

  const submitTx = async (payload) => {
    try {
      const res = await api.post("/transactions", payload);
      const txId = res.data.id;
      if (txDialog?.doc?.id && txId) {
        await api.put(`/documents/${txDialog.doc.id}`, { transaction_id: txId });
      }
      toast.success("Movimento creato e collegato");
      setTxDialog(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione movimento");
    }
  };

  const stats = useMemo(() => {
    const byCat = {};
    items.forEach((d) => { byCat[d.category] = (byCat[d.category] || 0) + 1; });
    const expiring = items.filter((d) => d.days_to_expiry != null && d.days_to_expiry >= 0 && d.days_to_expiry <= 30).length;
    const expired = items.filter((d) => d.days_to_expiry != null && d.days_to_expiry < 0).length;
    return { total: items.length, byCat, expiring, expired };
  }, [items]);

  return (
    <div data-testid="documents-page">
      <PageHeader eyebrow="Archivio" title="Documenti"
        subtitle="Fatture, contratti, chiusure fiscali, certificati HACCP e corsi. Collega ogni documento al movimento, dipendente o fornitore relativo."
        actions={
          canWrite ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="upload-doc-btn" className="bg-neutral-900 text-[#FDFBF7]"><Upload size={14} strokeWidth={1.5} /> Carica documento</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-serif">Nuovo documento</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label>File</Label>
                  <Input data-testid="doc-file" ref={fileRef} type="file" required onChange={(e) => { const f2 = e.target.files?.[0]; setFile(f2); setOcrData(null); if (!form.name && f2) setForm((f) => ({ ...f, name: f2.name })); }} />
                  {file && <div className="text-xs text-neutral-500 mt-1 flex items-center justify-between gap-2">
                    <span>{file.name} · {fmtBytes(file.size)}</span>
                    {(file.type?.includes("pdf") || file.name?.toLowerCase().endsWith(".pdf")) && (
                      <button type="button" onClick={runOcr} disabled={ocrLoading} data-testid="doc-ocr-btn"
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-neutral-900 text-[#FDFBF7] rounded hover:bg-neutral-800 disabled:opacity-50">
                        <Wand2 size={11} strokeWidth={1.8} /> {ocrLoading ? "Analisi AI…" : "Estrai dati da fattura"}
                      </button>
                    )}
                  </div>}
                  {ocrData && (
                    <div className="mt-2 border border-emerald-300 bg-emerald-50 rounded p-3 text-xs space-y-1" data-testid="ocr-result">
                      <div className="flex items-center gap-1 label-eyebrow text-emerald-900"><Sparkles size={11} /> Dati estratti</div>
                      {ocrData.supplier && <div><span className="text-neutral-500">Fornitore:</span> <b>{ocrData.supplier}</b></div>}
                      {ocrData.invoice_number && <div><span className="text-neutral-500">N.:</span> {ocrData.invoice_number}</div>}
                      {ocrData.date && <div><span className="text-neutral-500">Data:</span> {ocrData.date}</div>}
                      {ocrData.total_amount != null && <div><span className="text-neutral-500">Totale:</span> <b>€ {Number(ocrData.total_amount).toFixed(2)}</b> {ocrData.vat_rate ? `(IVA ${ocrData.vat_rate}%)` : ""}</div>}
                      {ocrData.suggested_category && <div><span className="text-neutral-500">Categoria suggerita:</span> {ocrData.suggested_category}</div>}
                      <div className="text-neutral-500 italic pt-1">Verifica i dati prima di salvare. Dopo il caricamento potrai creare il movimento pre-compilato.</div>
                    </div>
                  )}
                </div>
                <div><Label>Titolo</Label><Input data-testid="doc-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Categoria</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="doc-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORY_LIST.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        {form.category && !CATEGORY_LIST.includes(form.category) && (
                          <SelectItem value={form.category}>{catLabel(form.category)}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Scadenza</Label><Input data-testid="doc-expiry" type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></div>
                </div>
                <div>
                  <Label>Collega a movimento (opzionale)</Label>
                  <Select value={form.transaction_id} onValueChange={(v) => setForm({ ...form, transaction_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="doc-tx"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Nessuno —</SelectItem>
                      {transactions.slice(0, 100).map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.date} · {t.description || t.category} · € {Number(t.amount).toFixed(2)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Dipendente (opzionale)</Label>
                    <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v === "none" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Nessuno —</SelectItem>
                        {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Fornitore (opzionale)</Label>
                    <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v === "none" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Nessuno —</SelectItem>
                        {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Tag (virgola)</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="es. 2026, iva, sdi" /></div>
                <div><Label>Note</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <DialogFooter><Button data-testid="doc-submit" type="submit" className="bg-neutral-900 text-[#FDFBF7]">Carica</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          ) : null
        } />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="border border-[#E2D9C8] rounded-lg p-4 bg-white">
          <div className="label-eyebrow">Totali</div>
          <div className="font-serif text-3xl mt-2">{stats.total}</div>
        </div>
        <div className={`border rounded-lg p-4 ${stats.expiring ? "bg-amber-50 border-amber-300" : "bg-white border-[#E2D9C8]"}`}>
          <div className="flex items-center gap-1 label-eyebrow"><AlertTriangle size={12} /> In scadenza (30gg)</div>
          <div className={`font-serif text-3xl mt-2 ${stats.expiring ? "text-amber-800" : ""}`}>{stats.expiring}</div>
        </div>
        <div className={`border rounded-lg p-4 ${stats.expired ? "bg-red-50 border-red-300" : "bg-white border-[#E2D9C8]"}`}>
          <div className="label-eyebrow">Scaduti</div>
          <div className={`font-serif text-3xl mt-2 ${stats.expired ? "text-red-800" : ""}`}>{stats.expired}</div>
        </div>
        <div className="border border-[#E2D9C8] rounded-lg p-4 bg-white">
          <div className="label-eyebrow">Categorie</div>
          <div className="font-serif text-3xl mt-2">{Object.keys(stats.byCat).length}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <Input data-testid="doc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca titolo, note, tag…" className="max-w-xs" />
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger data-testid="doc-filter-cat" className="max-w-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {CATEGORY_LIST.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant={expOnly ? "default" : "outline"} onClick={() => setExpOnly(!expOnly)} data-testid="filter-expiring"
          className={expOnly ? "bg-neutral-900 text-[#FDFBF7]" : ""}>
          Solo in scadenza (30gg)
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[#E2D9C8] rounded-lg p-10 text-center text-neutral-500">
          Nessun documento con questi filtri. Carica il primo con il pulsante in alto.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((d) => {
            const Icon = fileIcon(d.content_type);
            const tx = d.transaction_id ? transactions.find((t) => t.id === d.transaction_id) : null;
            const emp = d.employee_id ? employees.find((e) => e.id === d.employee_id) : null;
            const sup = d.supplier_id ? suppliers.find((s) => s.id === d.supplier_id) : null;
            const expired = d.days_to_expiry != null && d.days_to_expiry < 0;
            const expiring = d.days_to_expiry != null && d.days_to_expiry >= 0 && d.days_to_expiry <= 30;
            return (
              <div key={d.id} className={`border rounded-lg p-4 bg-white ${expired ? "border-red-300" : expiring ? "border-amber-300" : "border-[#E2D9C8]"}`} data-testid={`doc-${d.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`shrink-0 w-11 h-11 rounded flex items-center justify-center ${expired ? "bg-red-100" : expiring ? "bg-amber-100" : "bg-[#F3EDE1]"}`}>
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-lg leading-tight truncate">{d.name}</div>
                    <div className="text-xs text-neutral-500 mt-1">{catLabel(d.category)} · {fmtBytes(d.size)}</div>
                    {d.expiry_date && (
                      <div className={`text-xs mt-1 ${expired ? "text-red-700 font-medium" : expiring ? "text-amber-800" : "text-neutral-500"}`}>
                        Scadenza: {d.expiry_date.slice(0, 10)}
                        {d.days_to_expiry != null && (
                          expired ? ` (scaduto da ${-d.days_to_expiry}g)` : ` (fra ${d.days_to_expiry}g)`
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {(tx || emp || sup) && (
                  <div className="mt-3 space-y-1 text-xs">
                    {tx && <div className="flex items-center gap-1 text-neutral-600"><Link2 size={11} strokeWidth={1.5} /> Movimento {tx.date} · €{Number(tx.amount).toFixed(2)}</div>}
                    {emp && <div className="flex items-center gap-1 text-neutral-600"><Link2 size={11} strokeWidth={1.5} /> {emp.first_name} {emp.last_name}</div>}
                    {sup && <div className="flex items-center gap-1 text-neutral-600"><Link2 size={11} strokeWidth={1.5} /> {sup.name}</div>}
                  </div>
                )}
                {d.tags?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {d.tags.map((t) => <span key={t} className="text-xs px-2 py-0.5 bg-[#F3EDE1] rounded-full">{t}</span>)}
                  </div>
                )}
                {d.notes && <div className="mt-2 text-xs text-neutral-500 border-l-2 border-[#D7C4A3] pl-2">{d.notes}</div>}
                <div className="mt-3 pt-3 border-t border-[#E2D9C8] flex justify-between items-center">
                  <div className="text-xs text-neutral-400">{d.uploaded_at?.slice(0, 10)} · {d.uploaded_by}</div>
                  <div className="flex gap-1">
                    {d.ocr && !d.transaction_id && (
                      <button data-testid={`create-tx-${d.id}`} onClick={() => createTxFromOcr(d, d.ocr)}
                        className="text-xs inline-flex items-center gap-1 px-2 py-1 bg-neutral-900 text-[#FDFBF7] rounded hover:bg-neutral-800">
                        <Sparkles size={11} strokeWidth={1.8} /> Crea movimento
                      </button>
                    )}
                    <button data-testid={`download-${d.id}`} onClick={() => download(d.id, d.original_filename)}
                      className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-[#E2D9C8] rounded hover:border-neutral-900">
                      <Download size={12} strokeWidth={1.5} /> Scarica
                    </button>
                    {canDelete && (
                    <button onClick={() => del(d.id, d.name)} className="text-neutral-400 hover:text-red-700">
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog: Crea Movimento da OCR Fattura */}
      <Dialog open={!!txDialog} onOpenChange={(v) => !v && setTxDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2"><Sparkles size={16} strokeWidth={1.5} /> Nuovo movimento da fattura</DialogTitle>
          </DialogHeader>
          {txDialog && <OcrTxForm doc={txDialog.doc} extracted={txDialog.extracted} accounts={accounts} onSubmit={submitTx} onCancel={() => setTxDialog(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const EXPENSE_CATEGORIES = ["materie prime", "bevande", "personale", "affitto", "energia", "acqua", "internet", "assicurazioni", "commercialista", "software", "manutenzione", "attrezzature", "marketing", "imposte", "fornitori", "pulizie", "materiali di consumo", "altri costi"];

function OcrTxForm({ doc, extracted, accounts, onSubmit, onCancel }) {
  const [txForm, setTxForm] = useState({
    type: "out",
    amount: extracted?.total_amount != null ? String(extracted.total_amount) : "",
    account_id: accounts?.[0]?.id || "",
    category: extracted?.suggested_category && EXPENSE_CATEGORIES.includes(extracted.suggested_category) ? extracted.suggested_category : "fornitori",
    subcategory: extracted?.supplier || "",
    description: [extracted?.supplier, extracted?.invoice_number ? `Fatt. ${extracted.invoice_number}` : null].filter(Boolean).join(" · "),
    date: extracted?.date || new Date().toISOString().slice(0, 10),
    vat: extracted?.vat_rate != null ? Number(extracted.vat_rate) : 22,
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...txForm, amount: parseFloat(txForm.amount) }); }} className="space-y-3">
      <div className="p-3 bg-[#F3EDE1] rounded text-xs">
        <div className="label-eyebrow mb-1">Da documento</div>
        <div className="font-medium truncate">{doc.name}</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Tipo</Label>
          <Select value={txForm.type} onValueChange={(v) => setTxForm({ ...txForm, type: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="in">Entrata</SelectItem><SelectItem value="out">Uscita</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label>Importo (€)</Label><Input data-testid="ocr-tx-amount" type="number" step="0.01" required value={txForm.amount} onChange={(e) => setTxForm({ ...txForm, amount: e.target.value })} /></div>
      </div>
      <div>
        <Label>Conto</Label>
        <Select value={txForm.account_id} onValueChange={(v) => setTxForm({ ...txForm, account_id: v })}>
          <SelectTrigger data-testid="ocr-tx-account"><SelectValue placeholder="Seleziona conto" /></SelectTrigger>
          <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Categoria</Label>
          <Select value={txForm.category} onValueChange={(v) => setTxForm({ ...txForm, category: v })}>
            <SelectTrigger data-testid="ocr-tx-cat"><SelectValue /></SelectTrigger>
            <SelectContent>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Data</Label><Input type="date" value={txForm.date} onChange={(e) => setTxForm({ ...txForm, date: e.target.value })} /></div>
      </div>
      <div><Label>Descrizione</Label><Input value={txForm.description} onChange={(e) => setTxForm({ ...txForm, description: e.target.value })} /></div>
      <div><Label>IVA %</Label><Input type="number" value={txForm.vat} onChange={(e) => setTxForm({ ...txForm, vat: parseInt(e.target.value) })} /></div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Annulla</Button>
        <Button data-testid="ocr-tx-submit" type="submit" className="bg-neutral-900 text-[#FDFBF7]">Crea movimento</Button>
      </DialogFooter>
    </form>
  );
}
